// src/services/sessionManager.js
import {
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import toast from 'react-hot-toast';

class SessionManager {
  constructor() {
    this.idleTimer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();
    this.isIdle = false;
    this.callbacks = {
      onIdle: null,
      onWarning: null,
      onActivity: null
    };

    // Default timeouts (in milliseconds)
    this.timeouts = {
      shared: 30 * 60 * 1000,    // 30 minutes for shared devices
      personal: 120 * 60 * 1000,  // 120 minutes for personal devices
      warning: 5 * 60 * 1000      // 5 minutes warning before logout
    };

    this.initializeActivityTracking();
  }

  // Set Firebase Auth persistence based on Remember Me choice
  async setPersistence(rememberMe = false, deviceType = 'personal') {
    if (!auth) return;

    try {
      // For shared devices, never use local persistence regardless of Remember Me
      if (deviceType === 'shared') {
        await setPersistence(auth, browserSessionPersistence);
        console.log('🔒 Session persistence: Browser session only (shared device)');
        return;
      }

      // For personal devices, respect Remember Me choice
      const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistenceType);

      console.log(`🔒 Session persistence: ${rememberMe ? 'Local storage' : 'Session only'} (personal device)`);
    } catch (error) {
      console.error('Error setting persistence:', error);
      toast.error('Failed to configure session settings');
    }
  }

  // Determine device type based on user role and clinic settings
  getDeviceType(userRole, clinicSettings = {}) {
    // Check if clinic has configured shared device IPs/identifiers
    if (clinicSettings.sharedDeviceIPs && Array.isArray(clinicSettings.sharedDeviceIPs)) {
      // In a real implementation, you'd check the client IP
      // For now, we'll use role-based detection
    }

    // Role-based device type detection
    if (userRole === 'reception' || userRole === 'front_desk') {
      return 'shared';
    }

    // Default to personal for other roles
    return 'personal';
  }

  // Initialize activity tracking
  initializeActivityTracking() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, this.resetIdleTimer.bind(this), true);
    });
  }

  // Start idle timeout monitoring
  startIdleTimeout(deviceType = 'personal', userRole = null) {
    this.stopIdleTimeout(); // Clear any existing timers

    const timeoutDuration = this.timeouts[deviceType] || this.timeouts.personal;
    const warningDuration = timeoutDuration - this.timeouts.warning;

    console.log(`⏰ Starting idle timeout: ${timeoutDuration / 60000} minutes for ${deviceType} device`);

    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.showIdleWarning();
    }, warningDuration);

    // Set logout timer
    this.idleTimer = setTimeout(() => {
      this.handleIdleLogout();
    }, timeoutDuration);

    this.lastActivity = Date.now();
  }

  // Reset idle timer on user activity
  resetIdleTimer() {
    if (this.isIdle) return; // Don't reset if already idle

    this.lastActivity = Date.now();

    // Clear existing timers
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    // Restart timers with current device type
    const deviceType = this.getCurrentDeviceType();
    if (deviceType) {
      this.startIdleTimeout(deviceType);
    }

    // Notify activity callback
    if (this.callbacks.onActivity) {
      this.callbacks.onActivity();
    }
  }

  // Stop idle timeout monitoring
  stopIdleTimeout() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.isIdle = false;
  }

  // Show idle warning dialog
  showIdleWarning() {
    console.log('⚠️ Showing idle warning');
    this.isIdle = true;

    if (this.callbacks.onWarning) {
      this.callbacks.onWarning();
    } else {
      // Default warning behavior
      toast.error('You will be logged out in 5 minutes due to inactivity', {
        duration: 10000,
        position: 'top-center'
      });
    }
  }

  // Handle idle logout
  handleIdleLogout() {
    console.log('🚪 Handling idle logout');
    this.isIdle = true;

    if (this.callbacks.onIdle) {
      this.callbacks.onIdle();
    } else {
      // Default idle behavior - sign out
      if (auth?.currentUser) {
        auth.signOut();
        toast.error('You have been logged out due to inactivity');
      }
    }
  }

  // Set callbacks for idle events
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Get current device type (stored in session)
  getCurrentDeviceType() {
    return sessionStorage.getItem('deviceType') || 'personal';
  }

  // Set current device type
  setCurrentDeviceType(deviceType) {
    sessionStorage.setItem('deviceType', deviceType);
  }

  // Get session info
  getSessionInfo() {
    return {
      lastActivity: this.lastActivity,
      isIdle: this.isIdle,
      deviceType: this.getCurrentDeviceType(),
      timeouts: this.timeouts
    };
  }

  // Update timeout settings
  updateTimeouts(newTimeouts) {
    this.timeouts = { ...this.timeouts, ...newTimeouts };
  }

  // Clean up on logout
  cleanup() {
    this.stopIdleTimeout();
    sessionStorage.removeItem('deviceType');
    this.lastActivity = Date.now();
    this.isIdle = false;
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
